import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { all, call, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { normalize } from 'normalizr';
import { selectors, withCursor, withOffset } from '../../Stream';
import { FetchType } from '../../Stream/base/types';
import { NotificationType } from '../../Notifications';
import { ResourceType } from '../../types/resource';
import { getCurrentUserId, getIsLoggedIn, getIsMobile } from '../../publicSession/selectors';
import { restrictLoggedOut } from '../../redux/loggedout';
import { putEntities } from '../../redux/entities/actions';
import { deviationSchema } from '../../redux/normalizr/schemas/deviation';
import { hidePanel } from '../../DeviationPage/redux/actions';
import { requestPuppy } from '@wix/da-http-client/pkg/request';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiData } from '@wix/da-bi/pkg/events';
import { logBiEvent } from '../../biLogger/redux/saga';
import { getPageBiContext } from '../../biLogger/redux/selectors';
import { groupFoldersStreamType, groupsStreamType } from './constants';
import { FolderType } from './types';
import { addToGroupFolder, addToGroupFolderFail, addToGroupFolderSuccess, initGroupsPanel } from './actions';
import { getFolderStreamId, getGroupsStreamId } from './helpers';
import { addDeviationToGroupFolder, fetchGroupFolders } from './xhr';
import { addNotificationToQueue } from '../../Notifications/redux/actionCreators';
import { getDeviationNotificationQueue } from '../../DeviationPage/redux/helpers';
const cursorFetchAction = withCursor.actions.STREAM__WITH_CURSOR__FETCH;
const offsetFetchAction = withOffset.actions.STREAM__WITH_OFFSET__FETCH;
export const GROUPS_PER_FETCH = 24;
export const GROUP_FOLDERS_PER_FETCH = 24;
// /developers/console/papi/da-deviation/dadeviation_groups/262c19bc5c3ed1ed9e6e1e55828095d4
const GROUPS_ENDPOINT_URL = 'groups';
export function* handleInit() {
    const isLoggedIn = yield select(getIsLoggedIn);
    if (!isLoggedIn) {
        return;
    }
    // check if it's already init
    const streamId = getGroupsStreamId();
    const userId = yield select(getCurrentUserId);
    const existingStreamParams = yield select(selectors.getStreamParams, streamId);
    if (existingStreamParams && existingStreamParams.userId === userId) {
        // already initialized, we're good
        return;
    }
    yield put(withCursor.actions.initialize({
        streamId,
        itemsPerFetch: GROUPS_PER_FETCH,
        items: [],
        hasMore: true,
        streamParams: {
            type: groupsStreamType,
            userId
        }
    }));
    yield put(withCursor.actions.fetch(streamId));
}
function* handleFetch({ streamId }) {
    const streamParams = yield select(selectors.getStreamParams, streamId);
    if (!streamParams) {
        return;
    }
    switch(streamParams.type){
        case groupsStreamType:
            yield call(handleFetchGroups, streamId, streamParams);
            break;
        case groupFoldersStreamType:
            yield call(handleFetchFolders, streamId, streamParams);
            break;
        default:
    }
}
function* handleFetchFolders(streamId, streamParams) {
    const isLoggedIn = yield select(getIsLoggedIn);
    if (!isLoggedIn) {
        yield put(restrictLoggedOut({
            restrictedActionName: ReferralType.ADD_TO_GROUP
        }));
        return;
    }
    yield delay(300);
    const result = yield call(fetchGroupFolders, streamParams.groupId, streamParams.folderType);
    if (!result) {
        yield put(withOffset.actions.fetchFailure({
            streamId,
            fetchType: FetchType.Next,
            errorMsg: 'Failed to fetch folders'
        }));
        return;
    }
    yield put(withOffset.actions.fetchSuccessDeduped({
        streamId,
        dedupeFunction: (item, existingItems)=>!existingItems.some((i)=>i.folderId === item.folderId),
        items: result.results,
        hasMore: false
    }));
}
export function* handleFetchGroups(streamId, streamParams) {
    var _result_results;
    const isLoggedIn = yield select(getIsLoggedIn);
    if (!isLoggedIn) {
        yield put(restrictLoggedOut({
            restrictedActionName: ReferralType.ADD_TO_GROUP
        }));
        return;
    }
    yield delay(300);
    const cursor = yield select(withCursor.selectors.getCursor, streamId);
    const result = yield call(requestPuppy, {
        method: 'get',
        url: GROUPS_ENDPOINT_URL,
        params: {
            cursor,
            limit: GROUPS_PER_FETCH
        }
    }, fakeFetchGroups, 'dadeviation');
    if (!result) {
        yield put(withCursor.actions.fetchFailure({
            streamId,
            fetchType: FetchType.Next,
            errorMsg: 'Failed to fetch groups'
        }));
        return;
    }
    // update groups stream, and init folder streams for all fetched groups
    yield all([
        put(withCursor.actions.fetchSuccessDeduped({
            streamId,
            dedupeFunction: (item, existingItems)=>!existingItems.some((i)=>i.userId === item.userId),
            items: result.results,
            hasMore: ((_result_results = result.results) === null || _result_results === void 0 ? void 0 : _result_results.length) >= GROUPS_PER_FETCH,
            cursor: result.cursor
        })),
        ...result.results.map((group)=>[
                FolderType.Gallery,
                FolderType.Collection
            ].map((folderType)=>{
                const folderStreamId = getFolderStreamId(folderType, group.userId);
                const folderStreamParams = {
                    type: groupFoldersStreamType,
                    folderType,
                    userId: streamParams.userId,
                    groupId: group.userId
                };
                return put(withOffset.actions.initialize({
                    streamId: folderStreamId,
                    itemsPerFetch: GROUP_FOLDERS_PER_FETCH,
                    items: [],
                    hasMore: true,
                    streamParams: folderStreamParams
                }));
            })).reduce((flattened, current)=>[
                ...flattened,
                ...current
            ], [])
    ]);
}
function fakeFetchGroups() {
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line global-require
        const groups = require('@wix/da-sample-data').groups;
        return {
            results: groups
        };
    }
    return {
        results: []
    };
}
function* handleAddToGroup({ payload: { group, deviation, groupFolderId, actionbarType, biData = {} } }) {
    const isLoggedIn = yield select(getIsLoggedIn);
    const biContext = yield select(getPageBiContext);
    if (!isLoggedIn) {
        yield put(restrictLoggedOut({
            restrictedActionName: ReferralType.ADD_TO_GROUP
        }));
        return;
    }
    const result = yield call(addDeviationToGroupFolder, groupFolderId, deviation.deviationId);
    const notification = {
        success: result.success,
        error: result.errorDetails,
        needsVote: result.needsVote,
        deviation,
        group,
        groupFolderId,
        actionbarType
    };
    if (!result.success) {
        yield put(addToGroupFolderFail(deviation.deviationId, groupFolderId));
    } else {
        yield put(addToGroupFolderSuccess(deviation.deviationId, groupFolderId, !!result.needsVote));
        // need to update the stat if got added
        if (!result.needsVote && typeof result.deviationGroupCount !== undefined && deviation.extended && deviation.extended.stats) {
            const updatedDeviation = _object_spread_props(_object_spread({}, deviation), {
                extended: _object_spread_props(_object_spread({}, deviation.extended), {
                    stats: _object_spread_props(_object_spread({}, deviation.extended.stats), {
                        groups: deviation.extended.stats.groups + 1
                    })
                })
            });
            const { entities } = normalize(updatedDeviation, deviationSchema);
            yield put(putEntities({
                entities
            }));
        }
    }
    yield delay(1000);
    yield put(hidePanel('groups'));
    yield call(logBiEvent, BiData(_object_spread_props(_object_spread({}, biContext, biData), {
        evid: 342,
        deviationid: deviation.deviationId,
        groupid: group.useridUuid,
        typeid: groupFolderId.type === FolderType.Collection ? ResourceType.COLLECTION : ResourceType.GALLERY,
        itemid: groupFolderId.folderId
    })));
    const isMobile = yield select(getIsMobile);
    yield put(addNotificationToQueue(isMobile ? 'default' : getDeviationNotificationQueue(deviation), NotificationType.AddToGroup, notification));
}
export default function* groupsRootSaga() {
    yield all([
        takeEvery(initGroupsPanel, handleInit),
        // groups stream is cursor-based, folders stream is offset-based
        takeEvery(cursorFetchAction, handleFetch),
        takeEvery(offsetFetchAction, handleFetch),
        takeLatest(addToGroupFolder, handleAddToGroup)
    ]);
}
